/**
 * Unified status row computation for the Aircraft Compact Card.
 *
 * Replaces the old triple-component approach (ContractStatusRow + StatusPills + CardAlertBanner)
 * with a single sorted list of StatusRowDef items rendered as a vertical stack.
 */

import type { AllocationEnrichedData, StatusRowSeverity } from '@/types/aircraft-card'
import {
  formatCurrency,
  formatDate,
  formatRelativeDate,
  getDaysUntil,
  getHoursSince,
} from './format-business-data'

// ── Types ──────────────────────────────────────────────

/** Sort groups: contract first, fuel second, other warnings, info/away last */
export type StatusRowGroup = 'contract' | 'fuel' | 'other' | 'info'

const GROUP_ORDER: Record<StatusRowGroup, number> = {
  contract: 1,
  fuel: 2,
  other: 3,
  info: 4,
}

const SEVERITY_ORDER: Record<StatusRowSeverity, number> = {
  critical: 1,
  warning: 2,
  notice: 3,
  info: 4,
  ok: 5,
}

export interface DropdownAction {
  label: string
  /** Action key resolved in StatusRowStack */
  actionKey: string
  destructive?: boolean
}

export interface StatusRowDef {
  key: string
  severity: StatusRowSeverity
  /** Sort group: contract first, fuel second, other, info last */
  group: StatusRowGroup
  /** Lucide icon name — resolved to component in UI layer */
  iconName: string
  /** Optional secondary icon rendered inline before text */
  textIconName?: string
  text: string
  /** 0-100 progress value (fuel rows) */
  progressValue?: number
  tooltipLines?: string[]
  /** Identifies the primary action button (renew_lease, checkout_allocation, etc.) */
  actionType?: string
  /** Multiple inline action buttons (e.g. +Lease / +Stay). Takes precedence over actionType. */
  actions?: { key: string; label: string }[]
  /** Secondary actions shown in chevron dropdown */
  dropdownActions?: DropdownAction[]
  /** Tab or form to open on row body click */
  rowClickTarget?: string
}

// ── Helpers ────────────────────────────────────────────

function daysSinceCheckIn(checkIn: string): number {
  const d = new Date(checkIn)
  return Math.max(1, Math.ceil((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)))
}

/** Returns formatted time (e.g. "2:30 PM") if the timestamp has a non-midnight time, otherwise null */
function formatTimeIfSet(dateStr: string): string | null {
  const d = new Date(dateStr)
  if (d.getHours() === 0 && d.getMinutes() === 0) return null
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// ── Options ───────────────────────────────────────────

export interface ComputeStatusRowsOptions {
  /** When true, suppresses the departing_today/tomorrow rows (schedule component handles it) */
  hasDepartureSchedule?: boolean
}

// ── Main computation ───────────────────────────────────

export function computeStatusRows(data: AllocationEnrichedData, options?: ComputeStatusRowsOptions): StatusRowDef[] {
  const rows: StatusRowDef[] = []

  let hasContractWarning = false
  let hasFuelWarning = false

  // Lease dropdown actions (shared by expired & expiring rows)
  const leaseDropdownActions: DropdownAction[] = [
    { label: 'Edit Lease', actionKey: 'edit_lease' },
    { label: 'Terminate', actionKey: 'terminate_lease', destructive: true },
    { label: 'Check Out', actionKey: 'checkout_allocation', destructive: true },
  ]

  // ── CONTRACT GROUP ─────────────────────────

  // Lease expired (end date passed but status still active)
  if (data.leaseContract?.status === 'active' && data.leaseContract.endDate) {
    const days = getDaysUntil(data.leaseContract.endDate)
    if (days <= 0) {
      const daysAgo = Math.abs(days)
      const lease = data.leaseContract
      rows.push({
        key: 'lease_expired',
        severity: 'critical',
        group: 'contract',
        iconName: 'FileText',
        text: `Lease Expired · ${daysAgo}d ago`,
        tooltipLines: [
          lease.contractNumber ? `Contract ${lease.contractNumber}` : null,
          `${formatDate(lease.startDate)} — ${formatDate(lease.endDate!)}`,
        ].filter(Boolean) as string[],
        actionType: 'renew_lease',
        dropdownActions: leaseDropdownActions,
        rowClickTarget: 'lease-details',
      })
      hasContractWarning = true
    }
  }

  // Lease terminated
  if (data.leaseContract?.status === 'terminated') {
    const allocationStillActive = data.allocation.status !== 'completed'
    rows.push({
      key: 'lease_terminated',
      severity: 'critical',
      group: 'contract',
      iconName: 'FileText',
      text: allocationStillActive ? 'Lease Terminated · Check Out?' : 'Lease Terminated',
      tooltipLines: data.leaseContract.terminationReason
        ? [data.leaseContract.terminationReason]
        : undefined,
      actionType: allocationStillActive ? 'checkout_allocation' : undefined,
      dropdownActions: [
        { label: 'Edit Lease', actionKey: 'edit_lease' },
        ...(allocationStillActive
          ? [{ label: 'Check Out', actionKey: 'checkout_allocation', destructive: true } as DropdownAction]
          : []),
      ],
      rowClickTarget: 'lease-details',
    })
    hasContractWarning = true
  }

  // Lease completed
  if (data.leaseContract?.status === 'completed') {
    const allocationStillActive = data.allocation.status !== 'completed'
    rows.push({
      key: 'lease_completed',
      severity: allocationStillActive ? 'warning' : 'info',
      group: 'contract',
      iconName: 'FileText',
      text: allocationStillActive ? 'Lease Completed · Check Out?' : 'Lease Completed',
      actionType: allocationStillActive ? 'checkout_allocation' : undefined,
      dropdownActions: [
        { label: 'View Lease', actionKey: 'view_lease' },
        ...(allocationStillActive
          ? [{ label: 'Check Out', actionKey: 'checkout_allocation', destructive: true } as DropdownAction]
          : []),
        { label: 'Create New Lease', actionKey: 'create_lease' },
      ],
      rowClickTarget: 'lease-details',
    })
    hasContractWarning = true
  }

  // Lease cancelled
  if (data.leaseContract?.status === 'cancelled') {
    rows.push({
      key: 'lease_cancelled',
      severity: 'info',
      group: 'contract',
      iconName: 'FileText',
      text: 'Lease Cancelled',
      actions: [
        { key: 'create_lease', label: '+ New Lease' },
        { key: 'create_transient', label: '+ Stay' },
      ],
      rowClickTarget: 'lease-details',
    })
    hasContractWarning = true
  }

  // Lease other non-active statuses (draft, pending_signature, expiring, expired handled elsewhere)
  if (
    data.leaseContract &&
    !['active', 'terminated', 'completed', 'cancelled', 'expired', 'expiring'].includes(data.leaseContract.status)
  ) {
    rows.push({
      key: 'lease_inactive',
      severity: 'info',
      group: 'contract',
      iconName: 'FileText',
      text: `Lease ${data.leaseContract.status}`,
      rowClickTarget: 'lease-details',
    })
    hasContractWarning = true
  }

  // Contract expiring (within 30d, not yet expired)
  if (data.leaseContract?.status === 'active' && data.leaseContract.endDate) {
    const days = getDaysUntil(data.leaseContract.endDate)
    if (days > 0 && days <= 30) {
      rows.push({
        key: 'contract_expiring',
        severity: days <= 7 ? 'critical' : 'warning',
        group: 'contract',
        iconName: 'FileWarning',
        text: `Lease expires ${formatRelativeDate(data.leaseContract.endDate)}`,
        tooltipLines: [
          data.leaseContract.contractNumber
            ? `Contract ${data.leaseContract.contractNumber}`
            : null,
          `End: ${formatDate(data.leaseContract.endDate)}`,
        ].filter(Boolean) as string[],
        actionType: 'renew_lease',
        dropdownActions: leaseDropdownActions,
        rowClickTarget: 'lease-details',
      })
      hasContractWarning = true
    }
  }

  // No contract (on-field > 24h)
  if (!data.leaseContract && !data.transientStay) {
    if (data.allocation.startsAt && getHoursSince(data.allocation.startsAt) > 24) {
      const hasNoOwner = !data.aircraft.owner && !data.aircraft.ownerName
      rows.push({
        key: 'no_contract',
        severity: 'critical',
        group: 'contract',
        iconName: 'FilePlus',
        text: hasNoOwner ? 'No Contract · No Owner' : 'No Contract',
        tooltipLines: hasNoOwner
          ? ['No lease, transient booking, or owner assigned']
          : ['No lease or transient booking'],
        actions: hasNoOwner
          ? [{ key: 'complete_setup', label: 'Complete Setup' }]
          : [
              { key: 'create_lease', label: 'Create Lease' },
              { key: 'create_transient', label: 'Create Stay' },
            ],
        dropdownActions: hasNoOwner
          ? [
              { label: '+ Lease', actionKey: 'create_lease' },
              { label: '+ Stay', actionKey: 'create_transient' },
            ]
          : undefined,
      })
      hasContractWarning = true
    }
  }

  // Overstaying transient (contract group since it's about the booking)
  if (
    data.transientStay?.expectedCheckOut &&
    data.transientStay.status === 'checked_in' &&
    !data.transientStay.actualCheckOut
  ) {
    const days = getDaysUntil(data.transientStay.expectedCheckOut)
    if (days < 0) {
      const hours = Math.round(getHoursSince(data.transientStay.expectedCheckOut))
      rows.push({
        key: 'overstaying',
        severity: 'warning',
        group: 'contract',
        iconName: 'Timer',
        text: `Overstaying ${hours}h past checkout`,
        tooltipLines: [`Expected checkout: ${formatDate(data.transientStay.expectedCheckOut)}`],
        actionType: 'checkout_allocation',
        dropdownActions: [
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Check Out', actionKey: 'checkout_allocation' },
        ],
        rowClickTarget: 'transient-details',
      })
    }
  }

  // Contract OK — lease active, not expiring
  if (data.leaseContract?.status === 'active' && !hasContractWarning) {
    const lease = data.leaseContract
    const endPart = lease.endDate ? ` · ends ${formatDate(lease.endDate)}` : ''
    rows.push({
      key: 'contract_ok',
      severity: 'ok',
      group: 'contract',
      iconName: 'CheckCircle',
      text: `Lease ${formatCurrency(lease.monthlyRate)}/mo${endPart}`,
      tooltipLines: [
        lease.contractNumber ? `Contract ${lease.contractNumber}` : null,
        lease.startDate && lease.endDate
          ? `${formatDate(lease.startDate)} — ${formatDate(lease.endDate)}`
          : lease.startDate
            ? `From ${formatDate(lease.startDate)}`
            : null,
      ].filter(Boolean) as string[],
      rowClickTarget: 'lease-details',
    })
  }

  // Transient OK / Last Day — checked in, not overstaying
  if (
    data.transientStay?.status === 'checked_in' &&
    !rows.some((r) => r.key === 'overstaying')
  ) {
    const ts = data.transientStay
    const daysUntilCheckout = getDaysUntil(ts.expectedCheckOut)
    const isLastDay = daysUntilCheckout === 0

    if (isLastDay) {
      // Last day — warning with checkout action (mirrors lease expiring pattern)
      const checkoutTime = formatTimeIfSet(ts.expectedCheckOut)
      const timePart = checkoutTime ? ` · ${checkoutTime}` : ''
      rows.push({
        key: 'transient_last_day',
        severity: 'warning',
        group: 'contract',
        iconName: 'Clock',
        text: `Transient · Last Day${timePart} · Check Out?`,
        tooltipLines: [
          ts.bookingNumber ? `Booking ${ts.bookingNumber}` : null,
          `Checkout: ${formatDate(ts.expectedCheckOut)}`,
        ].filter(Boolean) as string[],
        actionType: 'checkout_allocation',
        dropdownActions: [
          { label: 'Extend Stay', actionKey: 'extend_stay' },
          { label: 'View Booking', actionKey: 'view_transient' },
        ],
        rowClickTarget: 'transient-details',
      })
    } else {
      // Normal stay in progress
      const rawDayNum = daysSinceCheckIn(ts.checkIn)
      const dayNum = ts.nightsCount ? Math.min(rawDayNum, ts.nightsCount) : rawDayNum
      const text = ts.nightsCount
        ? `Transient · Day ${dayNum} of ${ts.nightsCount}`
        : 'Transient · Checked In'
      rows.push({
        key: 'transient_ok',
        severity: 'ok',
        group: 'contract',
        iconName: 'CheckCircle',
        text,
        tooltipLines: [
          ts.bookingNumber ? `Booking ${ts.bookingNumber}` : null,
          `Check-in: ${formatDate(ts.checkIn)}`,
          `Checkout: ${formatDate(ts.expectedCheckOut)}`,
        ].filter(Boolean) as string[],
        dropdownActions: [
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Check Out', actionKey: 'checkout_allocation' },
        ],
        rowClickTarget: 'transient-details',
      })
    }
  }

  // Transient booked (reserved/confirmed) — split into normal vs overdue
  if (
    data.transientStay &&
    (data.transientStay.status === 'reserved' || data.transientStay.status === 'confirmed')
  ) {
    const daysUntilCheckIn = getDaysUntil(data.transientStay.checkIn)
    const isOverdue = daysUntilCheckIn < 0
    const daysLate = Math.abs(daysUntilCheckIn)

    if (isOverdue) {
      rows.push({
        key: 'transient_booked_overdue',
        severity: daysLate > 2 ? 'critical' : 'warning',
        group: 'contract',
        iconName: 'AlertTriangle',
        text: `Booking Overdue · ${daysLate}d late`,
        tooltipLines: [
          data.transientStay.bookingNumber ? `Booking ${data.transientStay.bookingNumber}` : null,
          `Expected arrival: ${formatDate(data.transientStay.checkIn)}`,
        ].filter(Boolean) as string[],
        actionType: 'no_show_transient',
        dropdownActions: [
          { label: 'Check In (Late)', actionKey: 'checkin_transient' },
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Cancel', actionKey: 'cancel_transient', destructive: true },
        ],
        rowClickTarget: 'transient-details',
      })
    } else if (daysUntilCheckIn === 0) {
      // Arriving today
      rows.push({
        key: 'arriving_today',
        severity: 'notice',
        group: 'contract',
        iconName: 'CalendarClock',
        text: 'Arriving today',
        tooltipLines: [
          data.transientStay.bookingNumber ? `Booking ${data.transientStay.bookingNumber}` : null,
          `Arrival: ${formatDate(data.transientStay.checkIn)}`,
          `Departure: ${formatDate(data.transientStay.expectedCheckOut)}`,
        ].filter(Boolean) as string[],
        actionType: 'checkin_transient',
        dropdownActions: [
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Cancel', actionKey: 'cancel_transient', destructive: true },
        ],
        rowClickTarget: 'transient-details',
      })
    } else if (daysUntilCheckIn === 1) {
      // Arriving tomorrow
      rows.push({
        key: 'arriving_tomorrow',
        severity: 'notice',
        group: 'contract',
        iconName: 'CalendarClock',
        text: 'Arriving tomorrow',
        tooltipLines: [
          data.transientStay.bookingNumber ? `Booking ${data.transientStay.bookingNumber}` : null,
          `Arrival: ${formatDate(data.transientStay.checkIn)}`,
          `Departure: ${formatDate(data.transientStay.expectedCheckOut)}`,
        ].filter(Boolean) as string[],
        actionType: 'checkin_transient',
        dropdownActions: [
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Cancel', actionKey: 'cancel_transient', destructive: true },
        ],
        rowClickTarget: 'transient-details',
      })
    } else {
      rows.push({
        key: 'transient_booked',
        severity: 'ok',
        group: 'contract',
        iconName: 'CalendarPlus',
        text: `Booked · ${formatDate(data.transientStay.checkIn)}`,
        tooltipLines: [
          data.transientStay.bookingNumber ? `Booking ${data.transientStay.bookingNumber}` : null,
          `Arrival: ${formatDate(data.transientStay.checkIn)}`,
          `Departure: ${formatDate(data.transientStay.expectedCheckOut)}`,
        ].filter(Boolean) as string[],
        actionType: 'checkin_transient',
        dropdownActions: [
          { label: 'Edit Stay', actionKey: 'edit_transient' },
          { label: 'Cancel', actionKey: 'cancel_transient', destructive: true },
          { label: 'No-Show', actionKey: 'no_show_transient', destructive: true },
        ],
        rowClickTarget: 'transient-details',
      })
    }
  }

  // No contract, fresh (<24h)
  if (!data.leaseContract && !data.transientStay && !hasContractWarning) {
    const hasNoOwner = !data.aircraft.owner && !data.aircraft.ownerName
    rows.push({
      key: 'no_contract_fresh',
      severity: 'critical',
      group: 'contract',
      iconName: 'FilePlus',
      text: hasNoOwner ? 'No Contract · No Owner' : 'No Contract',
      tooltipLines: hasNoOwner
        ? ['No lease, transient booking, or owner assigned']
        : ['Create a lease or transient booking'],
      actions: hasNoOwner
        ? [{ key: 'complete_setup', label: 'Complete Setup' }]
        : [
            { key: 'create_lease', label: '+ Lease' },
            { key: 'create_transient', label: '+ Stay' },
          ],
      dropdownActions: hasNoOwner
        ? [
            { label: '+ Lease', actionKey: 'create_lease' },
            { label: '+ Stay', actionKey: 'create_transient' },
          ]
        : undefined,
    })
  }

  // ── FUEL GROUP ─────────────────────────────

  // Fuel at risk (commitment-based only)
  if (data.fuelSummary?.isAtRisk && data.fuelSummary.commitmentGallonsMonthly != null) {
    const fs = data.fuelSummary
    rows.push({
      key: 'fuel_at_risk',
      severity: 'warning',
      group: 'fuel',
      iconName: 'Fuel',
      text: `Fuel ${fs.deliveredGallonsMonthly}/${fs.commitmentGallonsMonthly} gal · at risk`,
      progressValue: Math.min(fs.percentComplete!, 100),
      tooltipLines: [
        `${fs.deliveredGallonsMonthly} of ${fs.commitmentGallonsMonthly} gal delivered this month`,
        'Unlikely to meet commitment at current pace',
      ],
      actionType: 'log_fuel',
      rowClickTarget: 'fuel',
    })
    hasFuelWarning = true
  }

  // Fuel behind (commitment-based, not at risk, but < 70%)
  if (
    data.fuelSummary &&
    data.fuelSummary.commitmentGallonsMonthly != null &&
    !data.fuelSummary.isAtRisk &&
    data.fuelSummary.percentComplete != null &&
    data.fuelSummary.percentComplete < 70
  ) {
    const fs = data.fuelSummary
    rows.push({
      key: 'fuel_behind',
      severity: 'notice',
      group: 'fuel',
      iconName: 'Fuel',
      text: `Fuel ${fs.deliveredGallonsMonthly}/${fs.commitmentGallonsMonthly} gal`,
      progressValue: Math.min(fs.percentComplete!, 100),
      tooltipLines: [
        `${fs.deliveredGallonsMonthly} of ${fs.commitmentGallonsMonthly} gal delivered this month`,
      ],
      rowClickTarget: 'fuel',
    })
    hasFuelWarning = true
  }

  // Fuel OK — commitment-based, on track
  if (data.fuelSummary && data.fuelSummary.commitmentGallonsMonthly != null && !hasFuelWarning) {
    const fs = data.fuelSummary
    rows.push({
      key: 'fuel_ok',
      severity: 'ok',
      group: 'fuel',
      iconName: 'Fuel',
      text: `Fuel ${fs.deliveredGallonsMonthly}/${fs.commitmentGallonsMonthly} gal`,
      progressValue: Math.min(fs.percentComplete!, 100),
      tooltipLines: [
        `${fs.deliveredGallonsMonthly} of ${fs.commitmentGallonsMonthly} gal delivered this month`,
        `${fs.percentComplete}% of commitment met`,
      ],
      rowClickTarget: 'fuel',
    })
  }

  // Fuel activity without commitment (transient / no-contract aircraft)
  if (data.fuelSummary && data.fuelSummary.commitmentGallonsMonthly == null) {
    const fs = data.fuelSummary
    rows.push({
      key: 'fuel_activity',
      severity: 'info',
      group: 'fuel',
      iconName: 'Fuel',
      text: `Fuel ${fs.deliveredGallonsMonthly} gal this month`,
      tooltipLines: [
        `${fs.deliveredGallonsMonthly} gallons fueled this month`,
        'No commitment target',
      ],
      rowClickTarget: 'fuel',
    })
  }

  // ── OTHER WARNINGS GROUP ───────────────────

  // Collision
  if (data.allocation.hasCollision) {
    rows.push({
      key: 'collision',
      severity: 'critical',
      group: 'other',
      iconName: 'AlertTriangle',
      text: 'Collision detected',
      tooltipLines: ['Aircraft overlaps with another placement — reposition to resolve'],
      rowClickTarget: 'aircraft-details',
    })
  }

  // Insurance expired
  if (data.aircraft.insuranceExpiry) {
    const days = getDaysUntil(data.aircraft.insuranceExpiry)
    if (days < 0) {
      rows.push({
        key: 'insurance_expired',
        severity: 'critical',
        group: 'other',
        iconName: 'ShieldAlert',
        text: `Insurance expired ${formatRelativeDate(data.aircraft.insuranceExpiry)}`,
        tooltipLines: [`Expiry: ${formatDate(data.aircraft.insuranceExpiry)}`],
        actionType: 'edit_insurance',
        rowClickTarget: 'aircraft-details',
      })
    }
  }

  // Insurance expiring (within 30d, not yet expired)
  if (data.aircraft.insuranceExpiry) {
    const days = getDaysUntil(data.aircraft.insuranceExpiry)
    if (days > 0 && days <= 30) {
      rows.push({
        key: 'insurance_expiring',
        severity: days <= 7 ? 'critical' : 'warning',
        group: 'other',
        iconName: 'ShieldAlert',
        text: `Insurance expires ${formatRelativeDate(data.aircraft.insuranceExpiry)}`,
        tooltipLines: [`Expiry: ${formatDate(data.aircraft.insuranceExpiry)}`],
        actionType: 'edit_insurance',
        rowClickTarget: 'aircraft-details',
      })
    }
  }

  // Return overdue (>2h grace)
  if (data.allocation.presence === 'away' && data.allocation.expectedReturnAt) {
    const hours = getHoursSince(data.allocation.expectedReturnAt)
    if (hours > 2) {
      rows.push({
        key: 'return_overdue',
        severity: hours > 12 ? 'critical' : 'warning',
        group: 'other',
        iconName: 'Clock',
        text: `Return overdue (${Math.round(hours)}h late)`,
        tooltipLines: [`Expected: ${formatDate(data.allocation.expectedReturnAt)}`],
        actionType: 'mark_returned',
        rowClickTarget: 'schedule',
      })
    }
  }

  // Departing today/tomorrow — skip if a departure schedule already exists
  if (data.transientStay?.expectedCheckOut && data.transientStay.status === 'checked_in' && !options?.hasDepartureSchedule) {
    const days = getDaysUntil(data.transientStay.expectedCheckOut)
    if (days === 0) {
      const checkoutTime = formatTimeIfSet(data.transientStay.expectedCheckOut)
      rows.push({
        key: 'departing_today',
        severity: 'notice',
        group: 'other',
        iconName: 'CalendarClock',
        textIconName: 'PlaneTakeoff',
        text: checkoutTime
          ? `No departure scheduled · ${checkoutTime}`
          : 'No departure scheduled',
        tooltipLines: [
          data.transientStay.bookingNumber ? `Booking ${data.transientStay.bookingNumber}` : null,
          checkoutTime
            ? `Booking checkout: today at ${checkoutTime}`
            : `Checkout date: ${formatDate(data.transientStay.expectedCheckOut)}`,
          'No departure schedule set — add one to notify staff or check out',
        ].filter(Boolean) as string[],
        actionType: 'add_departure_schedule',
        dropdownActions: [
          { label: 'Schedule Departure', actionKey: 'add_departure_schedule' },
          { label: 'Check Out', actionKey: 'checkout_allocation', destructive: true },
          { label: 'View Booking', actionKey: 'view_transient' },
          { label: 'Extend Stay', actionKey: 'extend_stay' },
        ],
        rowClickTarget: 'transient-details',
      })
    } else if (days === 1) {
      const checkoutTime = formatTimeIfSet(data.transientStay.expectedCheckOut)
      rows.push({
        key: 'departing_tomorrow',
        severity: 'notice',
        group: 'other',
        iconName: 'Clock',
        text: checkoutTime
          ? `Departing tomorrow at ${checkoutTime}`
          : 'Departing tomorrow',
        rowClickTarget: 'transient-details',
      })
    }
  }

  // Lease ending/ended — no departure scheduled
  if (
    data.leaseContract?.status === 'active' &&
    data.leaseContract.endDate &&
    !options?.hasDepartureSchedule
  ) {
    const daysLeft = getDaysUntil(data.leaseContract.endDate)
    if (daysLeft <= 30) {
      const isExpired = daysLeft <= 0
      rows.push({
        key: 'lease_no_departure',
        severity: isExpired ? 'warning' : 'notice',
        group: 'other',
        iconName: 'FileWarning',
        textIconName: 'PlaneTakeoff',
        text: 'No departure scheduled',
        tooltipLines: [
          data.leaseContract.contractNumber ? `Contract ${data.leaseContract.contractNumber}` : null,
          isExpired
            ? `Lease expired ${formatRelativeDate(data.leaseContract.endDate)}`
            : `Lease ends ${formatRelativeDate(data.leaseContract.endDate)}`,
          'No departure schedule set — add one or check out the aircraft',
        ].filter(Boolean) as string[],
        actionType: 'add_departure_schedule',
        dropdownActions: [
          { label: 'Schedule Departure', actionKey: 'add_departure_schedule' },
          { label: 'Check Out', actionKey: 'checkout_allocation', destructive: true },
        ],
      })
    }
  }

  // Lease terminated/completed — no departure scheduled
  if (
    data.leaseContract &&
    (data.leaseContract.status === 'terminated' || data.leaseContract.status === 'completed') &&
    data.allocation.status !== 'completed' &&
    !options?.hasDepartureSchedule
  ) {
    rows.push({
      key: 'lease_ended_no_departure',
      severity: 'warning',
      group: 'other',
      iconName: 'FileWarning',
      textIconName: 'PlaneTakeoff',
      text: 'No departure scheduled',
      tooltipLines: [
        data.leaseContract.contractNumber ? `Contract ${data.leaseContract.contractNumber}` : null,
        `Lease ${data.leaseContract.status} — aircraft still on field`,
        'Schedule a departure or check out the aircraft',
      ].filter(Boolean) as string[],
      actionType: 'add_departure_schedule',
      dropdownActions: [
        { label: 'Schedule Departure', actionKey: 'add_departure_schedule' },
        { label: 'Check Out', actionKey: 'checkout_allocation', destructive: true },
      ],
    })
  }

  // Special requests (transient)
  if (data.transientStay?.specialRequests) {
    const full = data.transientStay.specialRequests
    const truncated = full.length > 50 ? `${full.slice(0, 47)}...` : full
    rows.push({
      key: 'special_requests',
      severity: 'info',
      group: 'info',
      iconName: 'StickyNote',
      text: `Request: ${truncated}`,
      tooltipLines: full.length > 50 ? [full] : undefined,
      rowClickTarget: 'transient-details',
    })
  }

  // No owner
  if (!data.aircraft.owner && !data.aircraft.ownerName) {
    rows.push({
      key: 'no_owner',
      severity: 'critical',
      group: 'other',
      iconName: 'UserX',
      text: 'No owner · Assign?',
      actions: [{ key: 'complete_setup', label: 'Complete Setup' }],
    })
  }

  // ── Sort: group first, then severity within group ──
  rows.sort((a, b) => {
    const gDiff = GROUP_ORDER[a.group] - GROUP_ORDER[b.group]
    if (gDiff !== 0) return gDiff
    return SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  })

  return rows
}
